/**
 * Slice E (web) — Batch dashboard.
 *
 * Web port of the mobile `apps/mobile/app/batches/index.tsx` screen. Lists
 * the cooperative's batches (scoped server-side by the caller's
 * `cooperativeId`), with a status filter row, a tap-through to detail, and
 * an inline "New batch" form that POSTs /batches.
 *
 * Batches are coop-scoped aggregation buckets that contributions roll up
 * into — there is no crop-type on a batch (crop metadata lives on
 * contributions). Create takes only an optional note; cooperative_id is
 * derived from the caller server-side.
 *
 * Uses the generated orval client (`../api/generated/batches/batches`);
 * /batches is now in the OpenAPI spec (TD-070).
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  ListItem,
  Stack,
  StatusPill,
  Chip,
  Text,
  TextField,
  type StatusPillVariant,
} from '@fse/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getBatchesControllerListQueryKey,
  useBatchesControllerCreate,
  useBatchesControllerList,
} from '../api/generated/batches/batches';
import {
  type BatchDto,
  type BatchDtoStatus as BatchStatus,
} from '../api/generated/models';

const PAGE_SIZE = 50;

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

type StatusFilter = 'all' | BatchStatus;

// 'cancelled' is reachable but rare; mirror the mobile filter row which
// surfaces the happy-path statuses. 'all' still shows cancelled rows.
const STATUS_FILTERS: StatusFilter[] = ['all', 'open', 'locked', 'sold', 'settled'];

// i18n key per status; resolved via t() at render. The localized label text
// lives under batches.status.* in the locale JSON (flat dotted keys).
const STATUS_LABEL_KEY: Record<BatchStatus, string> = {
  open: 'batches.status.open',
  locked: 'batches.status.locked',
  sold: 'batches.status.sold',
  settled: 'batches.status.settled',
  cancelled: 'batches.status.cancelled',
};

// StatusPill variants per batch status. Mirrors the mobile StatusBadge palette
// intent on the Mango — Farmer Market semantic pill scale (open = active/in
// progress, locked = due/awaiting action, sold = sold/closed positively,
// settled = active/completed leaf, cancelled = overdue/alert).
const STATUS_PILL: Record<BatchStatus, StatusPillVariant> = {
  open: 'active',
  locked: 'due',
  sold: 'sold',
  settled: 'active',
  cancelled: 'overdue',
};

function shortId(id: string): string {
  return id.slice(0, 8);
}

export function BatchesScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [showCreate, setShowCreate] = useState(false);

  const listParams = { limit: PAGE_SIZE, offset: 0 };
  const batchesQuery = useBatchesControllerList(listParams);

  const visibleBatches = useMemo(() => {
    const rows = batchesQuery.data?.data ?? [];
    if (filter === 'all') return rows;
    return rows.filter((b) => b.status === filter);
  }, [batchesQuery.data, filter]);

  function onCreated() {
    queryClient.invalidateQueries({
      queryKey: getBatchesControllerListQueryKey(listParams),
    });
    setShowCreate(false);
  }

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Text variant="headline-medium" as="h2">
            {t('batches.title')}
          </Text>
          <Box sx={{ mt: 0.5 }}>
            <Text variant="body-medium" color="text.secondary">
              {t('batches.subtitle')}
            </Text>
          </Box>
        </Box>
        <Button variant="contained" onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? t('batches.cancel') : t('batches.newBatch')}
        </Button>
      </Box>

      {showCreate ? <CreateBatchForm onCreated={onCreated} /> : null}

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {STATUS_FILTERS.map((f) => (
          <Chip
            key={f}
            kind="filter"
            label={f === 'all' ? t('batches.filter.all') : t(STATUS_LABEL_KEY[f])}
            selected={filter === f}
            onClick={() => setFilter(f)}
          />
        ))}
      </Stack>

      {batchesQuery.isPending ? (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          </CardContent>
        </Card>
      ) : batchesQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => batchesQuery.refetch()}>
              {t('batches.retry')}
            </Button>
          }
        >
          {t('batches.loadError', { error: String(batchesQuery.error) })}
        </Alert>
      ) : visibleBatches.length === 0 ? (
        <Card>
          <CardHeader
            title={
              filter === 'all'
                ? t('batches.empty.allTitle')
                : t('batches.empty.filteredTitle', {
                    status: t(STATUS_LABEL_KEY[filter as BatchStatus]).toLowerCase(),
                  })
            }
          />
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {filter === 'all' ? t('batches.empty.allBody') : t('batches.empty.filteredBody')}
            </Text>
          </CardContent>
        </Card>
      ) : (
        <BatchesTable rows={visibleBatches} total={batchesQuery.data.pagination.total} />
      )}
    </Stack>
  );
}

interface CreateBatchFormProps {
  onCreated: () => void;
}

function CreateBatchForm({ onCreated }: CreateBatchFormProps) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const createMutation = useBatchesControllerCreate({
    mutation: {
      onSuccess: () => {
        setNote('');
        onCreated();
      },
    },
  });

  const submit = () => {
    const trimmed = note.trim();
    createMutation.mutate({ data: trimmed ? { note: trimmed } : {} });
  };

  return (
    <Card variant="outlined">
      <CardHeader
        title={t('batches.create.title')}
        subheader={t('batches.create.subtitle')}
      />
      <CardContent>
        <Stack spacing={2}>
          <TextField
            label={t('batches.create.noteLabel')}
            placeholder={t('batches.create.notePlaceholder')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={1}
            maxRows={4}
            slotProps={{ htmlInput: { maxLength: 500 } }}
            disabled={createMutation.isPending}
            fullWidth
          />

          {createMutation.isError ? (
            <Alert severity="error">
              {t('batches.create.error', { error: String(createMutation.error) })}
            </Alert>
          ) : null}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={submit}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? t('batches.create.opening') : t('batches.create.submit')}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

interface BatchesTableProps {
  rows: BatchDto[];
  total: number;
}

function BatchesTable({ rows, total }: BatchesTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const countLabel =
    total === 1 ? t('batches.count.one', { count: total }) : t('batches.count.other', { count: total });
  const filteredSuffix =
    rows.length < total ? ' ' + t('batches.count.filteredSuffix', { shown: rows.length }) : '';
  return (
    <Stack spacing={1.5}>
      <Text variant="body-medium" color="text.secondary">
        {countLabel}
        {filteredSuffix}
      </Text>
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {rows.map((batch, idx) => (
          <ListItem
            key={batch.id}
            leadingIcon="inventory_2"
            title={t('batches.row.title', { id: shortId(batch.id) })}
            supportingText={t('batches.row.supporting', {
              kg: batch.totalKg,
              contributions:
                batch.contributionCount === 1
                  ? t('batches.row.contributionsOne', { count: batch.contributionCount })
                  : t('batches.row.contributionsOther', { count: batch.contributionCount }),
              openedAt: dateFormatter.format(new Date(batch.openedAt)),
            })}
            trailing={
              <StatusPill variant={STATUS_PILL[batch.status]}>
                {t(STATUS_LABEL_KEY[batch.status])}
              </StatusPill>
            }
            onClick={() => navigate({ to: '/batches/$id', params: { id: batch.id } })}
            noDivider={idx === rows.length - 1}
          />
        ))}
      </Box>
    </Stack>
  );
}
